export type CashOutCaptureMode =
  | "PURCHASE_QUANTITY_AMOUNT"
  | "COG_QUANTITY_AMOUNT"
  | "AMOUNT"
  | "AMOUNT_NOTE_REVIEW";

export type CashOutCapturePolicy = {
  mode: CashOutCaptureMode;
  quantity_required: boolean;
  supplier_required?: boolean;
  supplier_capture_mode?: "UNSPECIFIED" | string;
  unit_name: string | null;
  suggested_unit_price: number | null;
  suggested_supplier_id?: string | null;
  suggested_supplier_name?: string | null;
  total_amount_required: boolean;
  note_required: boolean;
  detail_label: string;
};

export type CashOutRuntimeOption = {
  option_id: string;
  option_version_id: string;
  version_no: number;
  display_label: string;
  requires_note: boolean;
  manager_approval_required: boolean;
  is_quick_action: boolean;
  sort_order: number;
  capture_policy: CashOutCapturePolicy;
  ready_to_commit: boolean;
  block_reason: string | null;
};

export type CashOutActiveShift = {
  shift_id: string;
  shift_status: string;
  opening_cash: number | null;
  expected_cash: number | null;
  cash_out_total: number | null;
};

export type CashOutHistoryItem = {
  event_id: string;
  occurred_at: string;
  shift_id: string;
  shift_status: string;
  actor_employee_id: string;
  display_label: string;
  option_id: string;
  option_version_id: string;
  amount: number;
  quantity: number | null;
  unit_name: string | null;
  unit_price: number | null;
  note: string | null;
  route_code: string;
  downstream: Record<string, unknown>;
  lifecycle_status: "POSTED" | "VOIDED" | "CORRECTED" | string;
  replacement_event_id: string | null;
  void_event_id: string | null;
  can_manage: boolean;
};

export type CashOutRuntimeProjection = {
  ok: boolean;
  contract: "GET_POS_CASH_OUT_RUNTIME_V3" | string;
  runtime_capability: "ONLINE_REQUIRED";
  organization_id: string;
  branch_id: string;
  branch_code: string;
  branch_name: string;
  node_id: string;
  employee_id: string;
  can_commit: boolean;
  active_shift: CashOutActiveShift | null;
  branch_cash_context: {
    ready: boolean;
    reason_code: string | null;
  };
  purchase_suppliers?: unknown[];
  option_count: number;
  options: CashOutRuntimeOption[];
  recent_cash_outs: CashOutHistoryItem[];
  lifecycle_contract: "POS_CASH_OUT_LIFECYCLE_V1" | string;
  generated_at: string;
};

export type CashOutCommitReadback = {
  ok: boolean;
  contract: "COMMIT_POS_CASH_OUT_V4" | string;
  result_status: "POSTED" | "IDEMPOTENT_REPLAY" | string;
  lifecycle_status: "POSTED" | "VOIDED" | "CORRECTED" | string;
  event_id: string;
  request_key: string;
  occurred_at: string;
  organization_id: string;
  branch_id: string;
  node_id: string;
  shift_id: string;
  actor_employee_id: string;
  display_label: string;
  option_id: string;
  option_version_id: string;
  amount: number;
  quantity: number | null;
  unit_name: string | null;
  unit_price: number | null;
  note: string | null;
  downstream: Record<string, unknown>;
  classification_status: string;
  shift: {
    shift_status: string;
    expected_cash: number | null;
    cash_out_total: number | null;
    opening_cash: number | null;
  };
  read_after_write: true;
};

type GatewayEnvelope<T> = { ok: boolean; data?: T; error?: string };
type ClientOptions = { baseUrl: string; timeoutMs?: number };
type SessionInput = { deviceSecret: string; sessionToken: string };

export type CashOutCommitInput = SessionInput & {
  optionVersionId: string;
  totalAmount: number;
  requestKey: string;
  quantity?: number | null;
  note?: string | null;
  clientContext?: Record<string, unknown>;
};

export type CashOutVoidReadback = {
  ok: boolean;
  contract: "VOID_POS_CASH_OUT_V1" | string;
  result_status: "VOIDED" | "IDEMPOTENT_REPLAY" | string;
  void_event_id: string;
  original_event_id: string;
  reason: string;
  shift: CashOutActiveShift;
  original: CashOutCommitReadback;
  history: CashOutHistoryItem[];
};

export type CashOutCorrectionReadback = {
  ok: boolean;
  contract: "CORRECT_POS_CASH_OUT_V1" | string;
  result_status: "CORRECTED" | "IDEMPOTENT_REPLAY" | string;
  correction_event_id: string;
  original_event_id: string;
  replacement_event_id: string;
  replacement: CashOutCommitReadback;
  history: CashOutHistoryItem[];
};

export class PosCashOutGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: ClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  runtime(input: SessionInput) {
    return this.post<CashOutRuntimeProjection>("/api/pos/runtime/cash-out", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
    });
  }

  commit(input: CashOutCommitInput) {
    return this.post<CashOutCommitReadback>("/api/pos/runtime/cash-out/commit", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      option_version_id: input.optionVersionId,
      total_amount: input.totalAmount,
      quantity: input.quantity ?? null,
      note: input.note?.trim() || null,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  voidEntry(input: SessionInput & {
    eventId: string;
    reason: string;
    requestKey: string;
    clientContext?: Record<string, unknown>;
  }) {
    return this.post<CashOutVoidReadback>("/api/pos/runtime/cash-out/void", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      event_id: input.eventId,
      reason: input.reason.trim(),
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  correct(input: CashOutCommitInput & {
    originalEventId: string;
    reason: string;
  }) {
    return this.post<CashOutCorrectionReadback>("/api/pos/runtime/cash-out/correct", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      original_event_id: input.originalEventId,
      option_version_id: input.optionVersionId,
      total_amount: input.totalAmount,
      quantity: input.quantity ?? null,
      note: input.note?.trim() || null,
      reason: input.reason.trim(),
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  private async post<T>(path: string, body: Record<string, unknown>): Promise<GatewayEnvelope<T>> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "omit",
        signal: controller.signal,
      });
      const payload = (await response.json()) as GatewayEnvelope<T>;
      if (!response.ok) return { ok: false, error: payload.error ?? `GATEWAY_HTTP_${response.status}` };
      return payload;
    } catch (error) {
      return {
        ok: false,
        error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
